import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";

import MyButton from "../MyButton";
import MyPagination from "../MyPagination";
import MyTextField from "../inputs/MyTextField";
import { SiteContext } from "../../context/SiteProvider";

const MyTable = (props) => {
  const {
    headers,
    data = [],
    dataKey = "id",
    showIndex = false,
    canClick = false,
    onRowClick,
    searchProps,
    paginationProps,
    loading = false,
    showAddBtn = true,
    order = "desc",
    addText = "",
    tableClass = "",
  } = props;

  const { t } = useTranslation();
  const { isMobile } = useContext(SiteContext);

  const initValues = {
    keyword: "",
  };

  const methods = useForm({
    defaultValues: initValues,
  });

  const value = methods.watch("keyword");

  useEffect(() => {
    if (typeof searchProps?.setKeyword === "function") {
      searchProps.setKeyword(value);
    }
  }, [searchProps, value]);

  return (
    <div className="w-full">
      {searchProps && (
        <FormProvider {...methods}>
          <form className="mb-5 flex flex-wrap items-center md:gap-2 gap-1">
            {searchProps?.additionalFilter && searchProps.additionalFilter}

            {isMobile ? (
              <MyTextField
                debounce={500}
                name="keyword"
                placeholder={t("common.search.placeholder")}
                className="w-[180px]"
                inputStyle={{ paddingLeft: 4, paddingRight: 4 }}
                outlined={false}
                icon={
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-primary"
                    size="sm"
                  />
                }
              />
            ) : (
              <MyTextField
                debounce={500}
                name="keyword"
                placeholder={t("common.search.placeholder")}
                className="w-[250px]"
                outlined={false}
                icon={
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-primary"
                    size="sm"
                  />
                }
              />
            )}

            {(searchProps?.additionalButtons || showAddBtn) && (
              <div className="ml-auto flex gap-2 items-center">
                {searchProps?.additionalButtons &&
                  searchProps.additionalButtons}
                {showAddBtn && (
                  <MyButton
                    variant="contained"
                    color="primary"
                    size = {searchProps?.btnFontSize || ""}
                    className={`flex-1 max-w-[130px] ${searchProps?.btnClassName || ""}`}
                    onClick={searchProps?.onClickAdd}
                  >
                    {addText ? addText : t("common.buttons.write")}
                  </MyButton>
                )}
              </div>
            )}
          </form>
        </FormProvider>
      )}
      <div className="overflow-x-auto max-w-full">
        <table id={props?.id} className={`w-full ${tableClass}`}>
          <thead>
            <tr>
              {showIndex && (
                <th
                  key="idx"
                  className="md:py-6 py-5 px-2 font-semibold text-gray-dark bg-[#F8F8F8] border-t-2 border-t-primary md:text-lg sm:text-base text-sm"
                  style={{
                    textAlign: "center",
                    width: 60,
                  }}
                >
                  번호
                </th>
              )}
              {headers.map((header) => (
                <th
                  key={header.id}
                  className="md:py-6 py-5 px-2 font-semibold text-gray-dark bg-[#F8F8F8] border-t-2 border-t-primary md:min-w-0 min-w-max md:text-lg sm:text-base text-sm"
                  style={{
                    textAlign: "left",
                    ...header.style,
                  }}
                >
                  {header.title}
                </th>
              ))}
            </tr>
            {loading && (
              <tr>
                <td
                  colSpan={showIndex ? headers.length + 1 : headers.length}
                  className="p-0 h-1 overflow-hidden relative"
                >
                  <div
                    className="h-1 bg-info w-[60%] absolute top-0 opacity-30 rounded"
                    style={{
                      animation: "loading-bar 1.5s infinite",
                    }}
                  />
                </td>
              </tr>
            )}
          </thead>
          <tbody>
            {data?.length > 0 ? (
              data.map((row, idx) => (
                <tr
                  key={row[dataKey]}
                  onClick={() => (canClick ? onRowClick(row) : null)}
                  className={canClick ? "cursor-pointer" : ""}
                >
                  {showIndex && (
                    <td
                      key={row[dataKey] + "-idx"}
                      style={{
                        textAlign: "center",
                        ...row.style,
                      }}
                      className="md:py-[22px] sm:py-5 py-4 border-b border-gray-border text-gray-dark md:text-lg sm:text-base text-sm"
                    >
                      {order === "desc"
                        ? paginationProps
                          ? paginationProps.total -
                            paginationProps.size * (paginationProps.page - 1) -
                            idx
                          : data.length - idx
                        : paginationProps
                        ? paginationProps.size * (paginationProps.page - 1) +
                          idx +
                          1
                        : idx + 1}
                    </td>
                  )}
                  {headers.map((header) => (
                    <td
                      key={row[dataKey] + "-" + header.id}
                      style={{
                        ...header.style,
                        ...row.style,
                      }}
                      className="md:py-[22px] py-5 border-b border-gray-border text-gray-dark md:min-w-0 min-w-max md:text-lg sm:text-base text-sm"
                    >
                      {row[header.id] || "-"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={showIndex ? headers.length + 1 : headers.length}
                  className="text-center text-gray-dark py-10 md:text-lg sm:text-base text-sm"
                >
                  {!loading && t("common.text.noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {paginationProps && paginationProps?.maxPage > 1 && (
        <div className="flex justify-center mt-5">
          <MyPagination paginationProps={paginationProps} />
        </div>
      )}

      <style>
        {`
          @keyframes loading-bar {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>
    </div>
  );
};

export default MyTable;
